import { PropsWithChildren, useEffect } from 'react';
import StateMachineWrapper from '@tn/flows/StateMachineWrapper';
import DataLayer from '@tn/foundation/components/DataLayer/DataLayer';
import BuildInfo from '@tn/foundation/components/Dev/BuildInfo';
import { DevDrawer } from '@tn/foundation/components/Dev/DevDrawer';
import { DevTools } from '@tn/foundation/components/Dev/DevTools';
import { parseFederationRemotes } from '@tn/foundation/components/Dev/RemotesList';
import DevErrorBoundaryProbe from '@tn/foundation/components/ErrorBoundary/DevErrorBoundaryProbe';
import ErrorBoundary from '@tn/foundation/components/ErrorBoundary/ErrorBoundary';
import { PlayerServicesUrlOverrideBootstrap } from '@tn/foundation/entities/video/components/SmartTagPlayer/PlayerServicesUrlOverrideBootstrap';
import useProfilePickerOnBoot from '@tn/foundation/hooks/host/useProfilePickerOnBoot';
import useAutomaticTracking from '@tn/foundation/hooks/tracking/useAutomaticTracking';
import useConsentTracking from '@tn/foundation/hooks/tracking/useConsentTracking';
import { useTrackAppBaseEvents } from '@tn/foundation/hooks/tracking/useTrackAppBaseEvents';
import type { AbTestsExtraParams } from '@tn/foundation/interfaces/abtests';
import AppProvider from '@tn/foundation/providers/AppProvider';
import BannerProvider from '@tn/foundation/providers/BannerProvider';
import LoadingOverlayProvider from '@tn/foundation/providers/LoadingOverlayProvider';
import TrackingProvider, {
    TrackingDataType,
    TrackingProviderProps,
} from '@tn/foundation/providers/TrackingProvider';
import { isAppDebug } from '@tn/foundation/utils/appDebug';
import { EMPTY_STR } from '@tn/foundation/utils/constants/common';
import { GetLayout } from '@tn/foundation/utils/createLayout';
import { initCmpConsentBridge } from '@tn/foundation/utils/vendors/cmpConsentBridge';
import { NextPage } from 'next';
import { AppProps } from 'next/app';

import '@vds/core/dist/css/fonts.css';
export type NextPageWithLayout<P = unknown, IP = P> = NextPage<P, IP> & {
    getLayout?: GetLayout;
};

import { ABTestExperimentsProvider } from '@tn/abtests/providers/ABTestExperimentsProvider';
import type { DatadogRumConfig } from '@tn/foundation/hooks/datadog/useDatadogRum';
import { useTrackAppLaunch } from '@tn/foundation/hooks/events/useTrackAppLaunch';
import { useTrackAppLaunchCount } from '@tn/foundation/hooks/events/useTrackAppLaunchCount';
import { useTrackBfcacheRestore } from '@tn/foundation/hooks/events/useTrackBfcacheRestore';
import { useTrackLaunchReady } from '@tn/foundation/hooks/events/useTrackLaunchReady';
import type { DatadogLogsConfig } from '@tn/foundation/utils/server/getDatadogLogsConfig';
export type { DatadogLogsConfig, DatadogRumConfig };

export type CustomAppProps = AppProps & {
    Component: NextPageWithLayout;
    tealiumEnv: string;
    region: string;
    currentEnv?: string;
    newRelicBrowserAppId?: string;
    trackingData?: TrackingDataType;
    datadogRumConfig?: DatadogRumConfig;
    ssrViewportWidth?: number;
    datadogLogsConfig?: DatadogLogsConfig;
    remoteMfe?: string;
};

type AppShellProps = PropsWithChildren & {
    appName: TrackingProviderProps['appName'];
    vdsTheme?: 'light' | 'dark';
    trackingData?: TrackingDataType;
    ssrViewportWidth?: number | null;
    remoteMfe?: string;
    clientUserGuid?: string;
    // Explicit AB-tests extraParams bag for values only a specific page can resolve (e.g.
    // Upsell's CMS promo_code), threaded from pageProps — same pattern as clientUserGuid.
    abTestsExtraParams?: AbTestsExtraParams;
};

const InitTracking = () => {
    useEffect(() => {
        initCmpConsentBridge();
    }, []);
    useTrackAppLaunch();
    useAutomaticTracking();
    useTrackAppBaseEvents();
    useConsentTracking();
    useTrackLaunchReady();
    useTrackAppLaunchCount();
    useTrackBfcacheRestore();
    useProfilePickerOnBoot();
    return <></>;
};

export default function AppShell({
    children,
    appName = EMPTY_STR,
    vdsTheme: _vdsTheme = 'dark',
    ssrViewportWidth = null,
    trackingData,
    remoteMfe,
    clientUserGuid,
    abTestsExtraParams,
}: AppShellProps) {
    useEffect(() => {
        // Add global logRemotes function for debugging
        if (typeof window !== 'undefined' && isAppDebug()) {
            window.logRemotes = () => {
                console.table(parseFederationRemotes());
            };
        }
    }, []);

    /**
     * This is a workaround for the issue where the page renders before styles are loaded
     * if 'visibility: hidden' is bad for SEO, we can add UA / bot checks to this
     */
    // const isSSR = useIsSSR();

    return (
        <AppProvider ssrViewportWidth={ssrViewportWidth}>
            <ABTestExperimentsProvider
                clientUserGuid={clientUserGuid}
                extraParams={abTestsExtraParams}>
                <TrackingProvider appName={appName} trackingData={trackingData}>
                    <BannerProvider>
                        <LoadingOverlayProvider>
                            <StateMachineWrapper>
                                <ErrorBoundary>
                                    {isAppDebug() && <DevErrorBoundaryProbe />}
                                    {children}
                                </ErrorBoundary>
                                <BuildInfo remoteMfe={remoteMfe} />
                            </StateMachineWrapper>
                            {/*
                             * Session-scoped — keep outside StateMachineWrapperso they aren't torn
                             * down when it re-keys on flow change. BuildInfo stays inside to avoid a hydration mismatch.
                             */}
                            <InitTracking />
                            <PlayerServicesUrlOverrideBootstrap />
                            <DataLayer />
                        </LoadingOverlayProvider>
                    </BannerProvider>
                </TrackingProvider>
                <DevDrawer>
                    <DevTools remoteMfe={remoteMfe} />
                </DevDrawer>
            </ABTestExperimentsProvider>
        </AppProvider>
    );
}
